<script setup>
// SiteUp (backlog): Painel da IA reachable no settings do Captain — métricas da
// conta (ai_agent_metrics) + reports de erro (ai_agent_feedbacks) com resolver.
// Substitui o AgentMetricsSection órfão (nunca renderizado, removido).
// SiteUp (operacao): período/inbox nas métricas, falhas hoje, tokens/custo,
// kill switch da conta e teste do motor de IA.
// SiteUP CRM (IA medida pelo que ela faz): modo de operação por caixa,
// sugestões como número de primeira classe e a aba da IA do Kanban.
import { computed, h, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AiAgentMetricsAPI from 'dashboard/api/aiAgentMetrics';
import AiAgentFeedbacksAPI from 'dashboard/api/aiAgentFeedbacks';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import PropertyFollowupPanel from './PropertyFollowupPanel.vue';
import { useShellPeriod } from 'dashboard/routes/dashboard/settings/reports/composables/useShellPeriod';
import { nearestFixedPeriod } from 'dashboard/routes/dashboard/settings/reports/helpers/reportsPeriod';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// SiteUp (casa dos relatorios): o mesmo painel e montado como aba "IA" dentro
// de Relatorios. Ali a pagina ja tem cabecalho, abas e rolagem propria, entao
// o PageLayout do Captain (header fixo + main com rolagem) daria duas barras e
// dois scrolls. Embutido, o painel entrega so o corpo.
const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
});

const PlainLayout = {
  name: 'AiPanelPlainLayout',
  props: { isFetching: { type: Boolean, default: false } },
  setup(layoutProps, { slots }) {
    return () =>
      h('div', { class: 'flex flex-col' }, [
        layoutProps.isFetching
          ? h(
              'div',
              {
                class:
                  'flex items-center justify-center py-10 text-n-slate-11',
              },
              [h(Spinner)]
            )
          : slots.body?.(),
      ]);
  },
};

const route = useRoute();
const { t, locale } = useI18n();
const metrics = ref(null);
const feedbacks = ref([]);
const isFetching = ref(false);
const loadError = ref(null); // 'forbidden' | 'generic' | null

// SiteUp (operacao): filtros do painel. 'all' = comportamento antigo (tudo).
const period = ref('all');
const inboxId = ref('');
const periodOptions = computed(() => [
  {
    value: 'today',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.TODAY'),
  },
  {
    value: '7d',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_7_DAYS'),
  },
  {
    value: '30d',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_30_DAYS'),
  },
  // 'all' no backend e teto de 90 dias (METRICS_MAX_WINDOW_DAYS, por
  // desempenho): o rotulo diz o recorte real em vez de "Todo o período".
  {
    value: 'all',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_90_DAYS'),
  },
]);
const inboxes = useMapGetter('inboxes/getInboxes');
let latestMetricsRequestId = 0;

// Na casa dos relatorios o periodo e o seletor unico da casca. O backend so
// aceita hoje, 7, 30 e 90 dias: o painel usa o fixo mais proximo e a casca
// avisa qual e. Os botoes proprios ficam so no painel dentro do Captain.
const shellPeriod = useShellPeriod(next => {
  period.value = nearestFixedPeriod(next).value;
});
if (shellPeriod) {
  period.value = nearestFixedPeriod(shellPeriod.period.value).value;
}

// SiteUp (operacao): estado do kill switch / teste do motor.
const isTogglingPause = ref(false);
const isTestingEngine = ref(false);
const engineResult = ref(null); // { ok, model, latency_ms, error }
// Pausar a conta inteira e testar o motor (que gasta uma chamada real) sao
// acoes de incidente numa tela de leitura: ficam num bloco recolhido. O estado
// (ativa ou pausada, e quem mexeu) continua visivel com o bloco fechado.
const isOperationOpen = ref(false);
const pauseDialog = ref(null);
const pausing = computed(() => !metrics.value?.captain_paused);

// Quem pausou ou reativou por ultimo, lido do audit da conta. So aparece
// quando o ultimo movimento bate com o estado atual: pausa gravada fora da
// tela nao ganha autor inventado.
const pauseChangeLabel = computed(() => {
  const change = metrics.value?.captain_pause_change;
  if (!change?.at || !change.by) return '';
  if (Boolean(change.paused) !== Boolean(metrics.value?.captain_paused)) {
    return '';
  }
  const when = new Date(change.at);
  if (Number.isNaN(when.getTime())) return '';
  const intlLocale = toIntlLocale(locale?.value);
  const date = new Intl.DateTimeFormat(intlLocale, {
    day: '2-digit',
    month: '2-digit',
  }).format(when);
  const time = new Intl.DateTimeFormat(intlLocale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(when);
  return t(
    change.paused
      ? 'AI_AGENTS.AI_PANEL.OPERATION.PAUSED_BY'
      : 'AI_AGENTS.AI_PANEL.OPERATION.RESUMED_BY',
    { name: change.by, date, time }
  );
});

// Os reports sao revisados na fila da Central da IA (status, categoria e
// nota). O painel mostra so quantos estao novos e leva para la. Conta sem a
// Central continua vendo a lista antiga, senao perderia o unico lugar de
// revisao.
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const hasAiCenter = computed(() => {
  const check = isFeatureEnabledOnAccount.value;
  if (typeof check !== 'function') return false;
  return Boolean(
    check(Number(route.params.accountId), FEATURE_FLAGS.AI_ACTION_CENTER)
  );
});
// Reports da IA tem endereço próprio na Central (`/v2/ai-center/reports`).
const aiCenterReportsRoute = computed(() => ({
  name: 'ai_center_area',
  params: { accountId: route.params.accountId, area: 'reports' },
}));

const assistantId = computed(() => Number(route.params.assistantId));
// Embutido na casa dos relatorios nao existe assistente de origem: sem
// assistantId o botao Voltar levaria para uma rota sem parametro.
const backUrl = computed(() => {
  if (props.embedded || !assistantId.value) return '';
  return {
    name: 'captain_assistants_settings_index',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  };
});

const timeAgo = dateStr => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

// SiteUp: status do pipeline report → proposta Copilot (não confundir com resolved).
const feedbackStatusLabel = fb => {
  if (fb.status === 'pending') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_PENDING');
  }
  if (fb.status === 'processing') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_PROCESSING');
  }
  if (fb.status === 'proposal_generated') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_PROPOSAL_GENERATED');
  }
  if (fb.status === 'no_action') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_NO_ACTION');
  }
  if (fb.status === 'processed') {
    // DB has no separate "error" enum — job failures land as processed + processing_error.
    return fb.processing_error
      ? t('GENERAL.AI_FEEDBACK_PIPELINE_ERROR')
      : t('GENERAL.AI_FEEDBACK_PIPELINE_PROCESSED');
  }
  return fb.status || '—';
};

const feedbackMetadata = fb =>
  [
    fb.user_name || t('GENERAL.AI_FEEDBACK_USER_FALLBACK'),
    timeAgo(fb.created_at),
    feedbackStatusLabel(fb),
    fb.assistant_response_id
      ? t('GENERAL.AI_FEEDBACK_RESPONSE_ID', {
          id: fb.assistant_response_id,
        })
      : null,
    fb.processing_error,
  ]
    .filter(Boolean)
    .join(' · ');

const fmt = n => new Intl.NumberFormat('pt-BR').format(n || 0);

// SiteUP CRM (relatorios): ausencia de dado NUNCA aparece como zero. O backend manda
// usage/failures_today = null enquanto a conta nao tem nenhuma linha em
// ai_usage_daily; a tela mostra o marcador de vazio em vez de 0 e de R$ 0,00.
const EMPTY_MARK = '·';
// Mesmo contrato para qualquer número do painel: ausente (null/undefined) vira
// o marcador de vazio, porque zero é uma afirmação sobre o período e ausência não é.
const num = n => (n === null || n === undefined ? EMPTY_MARK : fmt(n));
const usageTracked = computed(() => !!metrics.value?.usage_tracked_since);
const trackedSinceLabel = computed(() => {
  const iso = metrics.value?.usage_tracked_since;
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `Medindo desde ${day}/${month}/${year}`;
});
const usageWindow = key => (usageTracked.value ? metrics.value?.usage?.[key] : null);
const usageTokens = key => {
  const window = usageWindow(key);
  if (!window) return EMPTY_MARK;
  return fmt((window.input_tokens || 0) + (window.output_tokens || 0));
};
// Texto na língua de quem opera (respostas, consumo), não do mecanismo
// (motor, tokens, falha técnica). Tokens e modelo ficam na linha de baixo.
const failuresLabel = computed(() =>
  usageTracked.value
    ? t('AI_AGENTS.AI_PANEL.USAGE.FAILURES_TODAY')
    : t('AI_AGENTS.AI_PANEL.USAGE.NOT_MEASURED')
);
const failuresToday = computed(() => {
  const value = metrics.value?.failures_today;
  return value === null || value === undefined ? EMPTY_MARK : fmt(value);
});

// SiteUP CRM: em que modo a IA opera, caixa a caixa. Sem isso o painel acusa
// "0 mensagens enviadas" em conta que, por configuração, só sugere.
const aiModes = computed(() => metrics.value?.ai_modes || null);
const modeRows = computed(() => aiModes.value?.rows || []);
const suggestionOnly = computed(() => aiModes.value?.suggestion_only === true);
const suggestions = computed(() => metrics.value?.suggestions || null);

const pct = (part, total) =>
  total ? `${Math.round((part / total) * 100)}%` : EMPTY_MARK;

const metricsParams = () => {
  const params = { period: period.value };
  if (inboxId.value) params.inbox_id = inboxId.value;
  return params;
};

// SiteUP CRM: aba Kanban. Endpoint próprio (a conta pode ter Kanban sem ter
// assistente), carregado sob demanda e com erro próprio: falha aqui nunca vira 0.
const tab = ref('ia');
const kanban = ref(null);
const kanbanError = ref(false);
const isFetchingKanban = ref(false);

const fetchKanban = async () => {
  isFetchingKanban.value = true;
  kanbanError.value = false;
  try {
    const { data } = await AiAgentMetricsAPI.kanban(metricsParams());
    kanban.value = data;
  } catch {
    kanban.value = null;
    kanbanError.value = true;
  } finally {
    isFetchingKanban.value = false;
  }
};

const kanbanFunnelsWithAi = computed(() =>
  (kanban.value?.funnels || []).filter(f => f.ai_enabled)
);
const kanbanFunnelsWithoutAi = computed(() =>
  (kanban.value?.funnels || []).filter(f => !f.ai_enabled)
);

watch(tab, value => {
  if (value === 'kanban' && !kanban.value && !isFetchingKanban.value) {
    fetchKanban();
  }
});

const fetchMetrics = async () => {
  latestMetricsRequestId += 1;
  const requestId = latestMetricsRequestId;
  try {
    const response = await AiAgentMetricsAPI.get(metricsParams());
    if (requestId !== latestMetricsRequestId) return false;

    metrics.value = response.data;
    return true;
  } catch (error) {
    if (requestId !== latestMetricsRequestId) return false;
    metrics.value = null;
    throw error;
  }
};

const fetchAll = async () => {
  isFetching.value = true;
  loadError.value = null;
  try {
    const [, f] = await Promise.all([
      fetchMetrics(),
      AiAgentFeedbacksAPI.get(),
    ]);
    feedbacks.value = f.data || [];
  } catch (error) {
    // Não engolir o erro: 403 (endpoints são admin-only) vira um aviso claro de
    // permissão; demais falhas mostram um alerta em vez de uma página em branco.
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      loadError.value = 'forbidden';
    } else {
      loadError.value = 'generic';
      useAlert('Não foi possível carregar o painel da IA.');
    }
  } finally {
    isFetching.value = false;
  }
};

// SiteUp (operacao): trocar período/caixa refaz só as métricas (feedbacks são globais).
// Falha aqui cai no mesmo bloco "Tentar de novo" da carga inicial:
// fetchMetrics zera as métricas e, sem loadError, o painel sumia inteiro
// (filtros e pausa da IA inclusive) sem nenhum jeito de sair além do F5.
watch([period, inboxId], async () => {
  if (loadError.value) return;
  try {
    await fetchMetrics();
  } catch {
    loadError.value = 'generic';
    useAlert(t('AI_AGENTS.AI_PANEL.METRICS_REFRESH_ERROR'));
    return;
  }
  // O relatório do Kanban também é recortado pelo período: refaz se estiver à vista.
  if (tab.value === 'kanban') await fetchKanban();
});

const resolveFeedback = async fb => {
  try {
    await AiAgentFeedbacksAPI.resolve(fb.id, !fb.resolved);
    fb.resolved = !fb.resolved;
  } catch {
    useAlert('Não foi possível atualizar.');
  }
};

// SiteUp (operacao): kill switch — pausa/retoma a IA da conta inteira, com
// confirmação explícita (é o botão de incidente). O diálogo do design system
// diz o que para e o que continua; o window.confirm não seguia o tema e
// sumia no celular. pauseDialog e pausing ficam junto do bloco "Operação".
const toggleAccountPause = () => pauseDialog.value?.open();

const confirmPauseToggle = async () => {
  const nextPaused = pausing.value;
  isTogglingPause.value = true;
  try {
    const { data } = await AiAgentMetricsAPI.togglePause(nextPaused);
    if (metrics.value) {
      metrics.value.captain_paused = data.captain_paused;
      metrics.value.captain_paused_by = data.captain_paused_by;
      metrics.value.captain_paused_at = data.captain_paused_at;
      // A troca feita agora é o último movimento; o audit só volta na
      // próxima carga, e até lá vale quem acabou de pausar.
      metrics.value.captain_pause_change = null;
    }
    useAlert(
      t(
        data.captain_paused
          ? 'AI_AGENTS.AI_PANEL.PAUSE.DONE'
          : 'AI_AGENTS.AI_PANEL.RESUME.DONE'
      )
    );
  } catch {
    useAlert(t('AI_AGENTS.AI_PANEL.PAUSE_ERROR'));
  } finally {
    isTogglingPause.value = false;
    pauseDialog.value?.close();
  }
};

// Quem pausou e quando, na faixa de status: "Pausada por Fulano às 14:32".
const pausedByText = computed(() => {
  const name = metrics.value?.captain_paused_by;
  const at = Date.parse(metrics.value?.captain_paused_at || '');
  if (!metrics.value?.captain_paused || !name || !at) return '';
  const lang = toIntlLocale(locale?.value || 'pt_BR');
  const when = new Date(at);
  const time = new Intl.DateTimeFormat(lang, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(when);
  if (when.toDateString() === new Date().toDateString()) {
    return t('AI_AGENTS.AI_PANEL.PAUSED_BY_TODAY', { name, time });
  }
  const date = new Intl.DateTimeFormat(lang, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(when);
  return t('AI_AGENTS.AI_PANEL.PAUSED_BY_DAY', { name, date });
});

// SiteUp (operacao): teste do motor — 1 completion mínima com o modelo real.
// Dentro de um assistente, testa ESTE assistente (cada um pode ter modelo
// próprio); embutido nos relatórios não há assistente e o backend usa o
// primeiro da conta.
const testEngine = async () => {
  isTestingEngine.value = true;
  engineResult.value = null;
  try {
    const { data } = await AiAgentMetricsAPI.engineHealth(assistantId.value);
    engineResult.value = data;
  } catch (error) {
    engineResult.value = {
      ok: false,
      error:
        error?.response?.data?.error ||
        t('AI_AGENTS.AI_PANEL.ENGINE.FAILED_GENERIC'),
    };
  } finally {
    isTestingEngine.value = false;
  }
};

onMounted(fetchAll);
</script>

<template>
  <component
    :is="embedded ? PlainLayout : PageLayout"
    header-title="Painel da IA"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        heading="Painel da IA"
        description="Métricas do agente e os erros reportados pelos atendentes (correções que entram no aprendizado da IA)."
      />
      <!-- Embutido nos relatorios, a casa diz de onde vem o numero desta aba
           e leva para a aba do robo (AiReports.vue). -->
      <slot name="source-note" />

      <div
        v-if="loadError === 'forbidden'"
        class="flex items-start gap-2 mt-7 px-3 py-2 rounded-xl bg-n-amber-2 text-n-amber-11 text-sm"
      >
        <span class="i-ri-lock-line size-4 shrink-0 mt-0.5" />
        <span>
          O Painel da IA é restrito a administradores. Peça a um administrador da
          conta para acessar as métricas e os reports de erro.
        </span>
      </div>

      <div
        v-else-if="loadError === 'generic'"
        class="flex items-center justify-between gap-3 mt-7 px-3 py-2 rounded-xl bg-n-slate-2 text-n-slate-11 text-sm"
      >
        <span>Não foi possível carregar o painel da IA.</span>
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-medium bg-n-teal-9/15 text-n-teal-11"
          @click="fetchAll"
        >
          Tentar de novo
        </button>
      </div>

      <!-- SiteUp (operacao): kill switch + teste do motor. Ficam num bloco
           recolhido "Operação da IA": o painel é de leitura, e pausar a conta
           inteira ou gastar uma chamada real no teste do motor não pode estar a
           um clique distraído. O estado fica à vista com o bloco fechado. -->
      <section
        v-if="!loadError && metrics"
        class="mt-7 rounded-2xl border"
        :class="
          metrics.captain_paused
            ? 'border-n-amber-7 bg-n-amber-2'
            : 'border-n-weak'
        "
        data-testid="ai-panel-operation"
      >
        <button
          type="button"
          class="flex w-full min-h-11 items-center gap-3 rounded-2xl px-3 py-2 text-start outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :aria-expanded="isOperationOpen ? 'true' : 'false'"
          aria-controls="ai-panel-operation-body"
          data-testid="ai-panel-operation-toggle"
          @click="isOperationOpen = !isOperationOpen"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :class="metrics.captain_paused ? 'bg-n-amber-9' : 'bg-n-teal-9'"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1">
            <span class="block text-sm font-medium text-n-slate-12">
              {{
                metrics.captain_paused
                  ? t('AI_AGENTS.AI_PANEL.STATUS_PAUSED')
                  : t('AI_AGENTS.AI_PANEL.STATUS_ACTIVE')
              }}
            </span>
            <!-- Quem mexeu por último: o audit da conta na carga; logo depois
                 de pausar aqui, quem acabou de pausar. -->
            <span
              v-if="pauseChangeLabel"
              class="block text-xs text-n-slate-11"
              data-testid="ai-panel-pause-change"
            >
              {{ pauseChangeLabel }}
            </span>
            <span
              v-else-if="pausedByText"
              class="block text-xs text-n-amber-11"
              data-testid="ai-paused-by"
            >
              {{ pausedByText }}
            </span>
          </span>
          <span class="shrink-0 text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.AI_PANEL.OPERATION.TITLE') }}
          </span>
          <span
            class="i-ph-caret-down size-4 shrink-0 text-n-slate-11 transition-transform"
            :class="{ 'rotate-180': isOperationOpen }"
            aria-hidden="true"
          />
        </button>
        <div
          v-if="isOperationOpen"
          id="ai-panel-operation-body"
          class="flex flex-col gap-3 border-t border-n-weak px-3 py-3"
        >
          <p class="text-xs text-n-slate-11">
            {{ t('AI_AGENTS.AI_PANEL.OPERATION.DESCRIPTION') }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              class="min-h-11"
              :label="
                isTestingEngine
                  ? t('AI_AGENTS.AI_PANEL.ENGINE.TESTING')
                  : t('AI_AGENTS.AI_PANEL.ENGINE.TEST')
              "
              :disabled="isTestingEngine"
              @click="testEngine"
            />
            <Button
              variant="faded"
              :color="metrics.captain_paused ? 'teal' : 'ruby'"
              size="sm"
              class="min-h-11"
              :label="
                metrics.captain_paused
                  ? t('AI_AGENTS.AI_PANEL.RESUME.BUTTON')
                  : t('AI_AGENTS.AI_PANEL.PAUSE.BUTTON')
              "
              :disabled="isTogglingPause"
              data-testid="ai-panel-pause-button"
              @click="toggleAccountPause"
            />
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.AI_PANEL.OPERATION.TEST_HINT') }}
          </p>
          <div
            v-if="engineResult"
            class="text-xs"
            :class="engineResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
            data-testid="ai-engine-result"
          >
            <p class="mb-0">
              <template v-if="engineResult.ok">
                {{
                  engineResult.assistant_name
                    ? t('AI_AGENTS.AI_PANEL.ENGINE.OK_ASSISTANT', {
                        name: engineResult.assistant_name,
                      })
                    : t('AI_AGENTS.AI_PANEL.ENGINE.OK')
                }}
              </template>
              <template v-else>
                {{ t('AI_AGENTS.AI_PANEL.ENGINE.FAILED') }}
              </template>
            </p>
            <p class="mb-0 mt-0.5 text-n-slate-10">
              <template v-if="engineResult.ok">
                {{
                  t('AI_AGENTS.AI_PANEL.ENGINE.DETAIL', {
                    model: engineResult.model,
                    latency: engineResult.latency_ms,
                  })
                }}
              </template>
              <template v-else>{{ engineResult.error }}</template>
            </p>
          </div>
        </div>
      </section>
      <Dialog
        ref="pauseDialog"
        :type="pausing ? 'alert' : 'edit'"
        :title="
          pausing
            ? t('AI_AGENTS.AI_PANEL.PAUSE.TITLE')
            : t('AI_AGENTS.AI_PANEL.RESUME.TITLE')
        "
        :description="pausing ? '' : t('AI_AGENTS.AI_PANEL.RESUME.DESCRIPTION')"
        :confirm-button-label="
          pausing
            ? t('AI_AGENTS.AI_PANEL.PAUSE.CONFIRM')
            : t('AI_AGENTS.AI_PANEL.RESUME.CONFIRM')
        "
        :is-loading="isTogglingPause"
        @confirm="confirmPauseToggle"
      >
        <div v-if="pausing" class="flex flex-col gap-2 text-sm text-n-slate-11">
          <p class="mb-0">{{ t('AI_AGENTS.AI_PANEL.PAUSE.STOPS') }}</p>
          <p class="mb-0">{{ t('AI_AGENTS.AI_PANEL.PAUSE.CONTINUES') }}</p>
        </div>
      </Dialog>

      <!-- SiteUp (operacao): seletor de período e de caixa -->
      <div v-if="!loadError && metrics" class="flex flex-wrap items-center gap-2 mt-4">
        <div
          v-if="!shellPeriod"
          class="grid w-full grid-cols-2 overflow-hidden rounded-xl border border-n-weak sm:flex sm:w-auto"
        >
          <button
            v-for="opt in periodOptions"
            :key="opt.value"
            type="button"
            class="min-h-11 px-3 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
            :class="
              period === opt.value
                ? 'bg-n-teal-9/15 text-n-teal-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="period = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
        <!-- O relatorio do Kanban nao e recortado por caixa (o endpoint ignora
             inbox_id): na aba Kanban o seletor fica desligado e diz isso, em
             vez de parecer que filtrou. -->
        <select
          v-model="inboxId"
          class="!mb-0 !h-11 !py-0 !pl-3 !pr-8 !text-xs !w-full sm:!w-auto rounded-xl border border-n-weak bg-transparent text-n-slate-11 disabled:opacity-60"
          :aria-label="t('AI_AGENTS.AI_PANEL.INBOX_FILTER')"
          :disabled="tab === 'kanban'"
          :aria-describedby="
            tab === 'kanban' ? 'ai-panel-kanban-inbox-hint' : undefined
          "
          data-testid="ai-panel-inbox-select"
        >
          <option value="">{{ t('AI_AGENTS.AI_PANEL.ALL_INBOXES') }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <span
          v-if="tab === 'kanban'"
          id="ai-panel-kanban-inbox-hint"
          class="text-xs text-n-slate-10"
          data-testid="ai-panel-kanban-inbox-hint"
        >
          {{ t('AI_AGENTS.AI_PANEL.KANBAN_NO_INBOX') }}
        </span>
      </div>

      <!-- SiteUP CRM: abas do painel. O Kanban tem IA própria (troca o cartão de
           etapa a partir da conversa) e ela não aparecia em lugar nenhum. -->
      <div
        v-if="!loadError && metrics"
        class="flex gap-1 mt-4 border-b border-n-weak"
      >
        <button
          v-for="opt in [
            { value: 'ia', label: 'IA nas conversas' },
            { value: 'kanban', label: 'IA no Kanban' },
          ]"
          :key="opt.value"
          type="button"
          class="min-h-11 px-3 text-xs font-medium -mb-px border-b-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
          :class="
            tab === opt.value
              ? 'border-n-teal-9 text-n-teal-11'
              : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
          "
          @click="tab = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>

      <!-- SiteUP CRM: em que MODO a IA opera, caixa a caixa. É o que dá sentido a
           todo o resto: conta em modo de sugestão não envia nada, e sem esta faixa
           o painel a mostrava como IA parada. -->
      <div
        v-if="!loadError && tab === 'ia' && modeRows.length"
        class="mt-4 rounded-2xl border border-n-weak p-3"
      >
        <p class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5">
          <span class="i-ph-sliders-horizontal size-4" />
          Como a IA opera nesta conta
        </p>
        <ul class="mt-2 flex flex-col gap-1">
          <li
            v-for="row in modeRows"
            :key="`${row.assistant_id}-${row.inbox_id}`"
            class="flex items-center justify-between gap-3 text-xs"
          >
            <span class="truncate text-n-slate-11">{{
              row.inbox_name || `Caixa ${row.inbox_id}`
            }}</span>
            <span
              class="shrink-0 rounded-md px-2 py-0.5 font-medium"
              :class="
                row.mode === 'off'
                  ? 'bg-n-slate-3 text-n-slate-11'
                  : row.sends
                    ? 'bg-n-teal-9/15 text-n-teal-11'
                    : 'bg-n-amber-9/15 text-n-amber-11'
              "
            >
              {{ row.mode_label }}
            </span>
          </li>
        </ul>
        <p v-if="suggestionOnly" class="mt-2 text-xs text-n-slate-10">
          Nenhuma caixa desta conta envia mensagem: a IA prepara o rascunho e
          quem responde é o atendente.
        </p>
      </div>

      <div
        v-if="!loadError && tab === 'ia' && metrics"
        class="grid grid-cols-1 gap-3 sm:grid-cols-3 mt-4"
      >
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-slate-12">{{ metrics.active_conversations }}</p>
          <p class="text-xs text-n-slate-10">Conversas com IA ativa</p>
        </div>
        <!-- Conta em modo de sugestão não pode mostrar "0 enviadas" como falha. -->
        <div
          v-if="suggestionOnly"
          class="rounded-2xl border border-n-weak p-4 text-center flex flex-col justify-center"
        >
          <p class="text-xs text-n-slate-11 font-medium">
            Esta conta não envia, ela sugere
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            As respostas saem pelo atendente, com o rascunho da IA ao lado.
          </p>
        </div>
        <div v-else class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-teal-11">
            {{ num(metrics.ai_messages_sent) }}
          </p>
          <p class="text-xs text-n-slate-10">Mensagens enviadas pela IA</p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-amber-11">{{ metrics.feedbacks_count }}</p>
          <!-- Os reports não seguem período nem caixa: o rótulo diz que é
               o total. -->
          <p
            class="text-xs text-n-slate-10"
            data-testid="ai-panel-feedbacks-label"
          >
            {{ t('AI_AGENTS.AI_PANEL.FEEDBACKS_TOTAL') }}
          </p>
        </div>
      </div>

      <!-- SiteUP CRM: sugestões como número de primeira classe, NUNCA somadas às
           mensagens enviadas: são atos diferentes da IA. -->
      <div
        v-if="!loadError && tab === 'ia' && suggestions"
        class="grid grid-cols-1 gap-3 sm:grid-cols-3 mt-3"
      >
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-teal-11"
            data-testid="ai-suggestions-total"
          >
            {{ num(suggestions.total) }}
          </p>
          <p class="text-xs text-n-slate-10">
            Sugestões entregues ao atendente
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-slate-12"
            data-testid="ai-suggestions-used"
          >
            {{ num(suggestions.used) }}
            <span class="text-sm font-semibold text-n-slate-10">
              ({{ pct(suggestions.used, suggestions.total) }})
            </span>
          </p>
          <p class="text-xs text-n-slate-10">
            Aproveitadas · {{ num(suggestions.untouched) }} sem editar ·
            {{ num(suggestions.edited) }} editadas
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-slate-12"
            data-testid="ai-suggestions-discarded"
          >
            {{ num(suggestions.discarded) }}
          </p>
          <p class="text-xs text-n-slate-10">
            Descartadas (o atendente escreveu outra coisa)
          </p>
        </div>
      </div>
      <p
        v-if="!loadError && tab === 'ia' && suggestions"
        class="mt-2 text-xs text-n-slate-10"
      >
        Só entra aqui a sugestão que chegou a uma resposta do atendente: o
        rascunho vive em cache temporário, então sugestão gerada e nunca
        respondida não deixa registro. Vocês estão vendo o aproveitamento, não o
        total gerado.
        <template v-if="suggestions.partial">
          Período grande: número parcial.
        </template>
      </p>

      <!-- SiteUP CRM: aba do Kanban -->
      <div v-if="!loadError && tab === 'kanban'" class="mt-4">
        <div
          v-if="kanbanError"
          class="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-n-slate-2 text-n-slate-11 text-sm"
        >
          <span>Não foi possível carregar o relatório do Kanban.</span>
          <button
            type="button"
            class="rounded-md px-2.5 py-1 text-xs font-medium bg-n-teal-9/15 text-n-teal-11"
            @click="fetchKanban"
          >
            Tentar de novo
          </button>
        </div>
        <p v-else-if="isFetchingKanban" class="text-xs text-n-slate-10">
          Carregando…
        </p>
        <template v-else-if="kanban">
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div class="rounded-2xl border border-n-weak p-4 text-center">
              <p
                class="text-2xl font-extrabold text-n-teal-11"
                data-testid="kanban-created-by-ai"
              >
                {{ num(kanban.created_by_ai) }}
              </p>
              <p class="text-xs text-n-slate-10">Cartões criados pela IA</p>
            </div>
            <div class="rounded-2xl border border-n-weak p-4 text-center">
              <p
                class="text-2xl font-extrabold text-n-slate-12"
                data-testid="kanban-touched-by-ai"
              >
                {{ num(kanban.touched_by_ai) }}
                <span class="text-sm font-semibold text-n-slate-10">
                  de {{ num(kanban.cards_total) }}
                </span>
              </p>
              <p class="text-xs text-n-slate-10">Cartões atualizados pela IA</p>
            </div>
            <div class="rounded-2xl border border-n-weak p-4 text-center">
              <p class="text-2xl font-extrabold text-n-slate-12">
                {{
                  kanban.last_ai_touch ? timeAgo(kanban.last_ai_touch) : EMPTY_MARK
                }}
              </p>
              <p class="text-xs text-n-slate-10">Desde a última ação da IA</p>
            </div>
          </div>

          <div class="mt-3 rounded-2xl border border-n-weak overflow-hidden">
            <table class="w-full text-xs">
              <thead class="bg-n-alpha-1 text-n-slate-10">
                <tr>
                  <th class="text-left font-medium px-3 py-2">Funil</th>
                  <th class="text-right font-medium px-3 py-2">IA</th>
                  <th class="text-right font-medium px-3 py-2">Criados</th>
                  <th class="text-right font-medium px-3 py-2">Atualizados</th>
                  <th class="text-right font-medium px-3 py-2">Cartões</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="f in [
                    ...kanbanFunnelsWithAi,
                    ...kanbanFunnelsWithoutAi,
                  ]"
                  :key="f.funnel_id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-11 truncate">
                    {{ f.funnel_name }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <span
                      class="rounded-md px-1.5 py-0.5 font-medium"
                      :class="
                        f.ai_enabled
                          ? 'bg-n-teal-9/15 text-n-teal-11'
                          : 'bg-n-slate-3 text-n-slate-10'
                      "
                    >
                      {{ f.ai_enabled ? 'ligada' : 'desligada' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right text-n-slate-12">
                    {{ f.created_by_ai ? fmt(f.created_by_ai) : EMPTY_MARK }}
                  </td>
                  <td class="px-3 py-2 text-right text-n-slate-12">
                    {{ f.touched_by_ai ? fmt(f.touched_by_ai) : EMPTY_MARK }}
                  </td>
                  <td class="px-3 py-2 text-right text-n-slate-10">
                    {{ fmt(f.cards_total) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="mt-2 text-xs text-n-slate-10">
            Não dá para dizer de qual etapa para qual a IA moveu cada cartão: o
            registro de troca de etapa não guarda quem moveu quando quem move é
            um robô, e nele a IA fica igual à automação de etapa e à importação.
            <template v-if="kanban.partial">
              Conta grande: número parcial.
            </template>
          </p>
        </template>
      </div>

      <!--
        SiteUP CRM (relatorios): falhas técnicas hoje + tokens/custo, somados da
        tabela ai_usage_daily. Enquanto a conta não tiver nenhuma linha medida,
        estes cartões mostram o marcador de vazio "·" e a frase "Consumo da
        IA ainda não medido", nunca 0 nem R$ 0,00.
      -->
      <div
        v-if="!loadError && tab === 'ia' && metrics"
        class="grid grid-cols-1 gap-3 sm:grid-cols-3 mt-3"
      >
        <div
          class="rounded-2xl border p-4 text-center"
          :class="metrics.failures_today ? 'border-n-ruby-7 bg-n-ruby-2' : 'border-n-weak'"
        >
          <p
            class="text-2xl font-medium"
            :class="metrics.failures_today ? 'text-n-ruby-11' : 'text-n-slate-12'"
            data-testid="ai-failures-today"
          >
            {{ failuresToday }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ failuresLabel }}
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-slate-12"
            data-testid="ai-tokens-today"
          >
            {{ usageTokens('today') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              usageTracked
                ? t('AI_AGENTS.AI_PANEL.USAGE.TODAY')
                : t('AI_AGENTS.AI_PANEL.USAGE.NOT_MEASURED')
            }}
          </p>
          <p v-if="usageTracked" class="text-xs text-n-slate-10">
            {{
              t('AI_AGENTS.AI_PANEL.USAGE.DETAIL', {
                runs: fmt(metrics.usage?.today?.runs),
              })
            }}
            <template v-if="metrics.usage?.today?.estimated_cost != null">
              ·
              {{
                t('AI_AGENTS.AI_PANEL.USAGE.COST', {
                  cost: metrics.usage.today.estimated_cost,
                })
              }}
            </template>
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-slate-12"
            data-testid="ai-tokens-7d"
          >
            {{ usageTokens('last_7_days') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              usageTracked
                ? t('AI_AGENTS.AI_PANEL.USAGE.LAST_7_DAYS')
                : t('AI_AGENTS.AI_PANEL.USAGE.NOT_MEASURED')
            }}
          </p>
          <p v-if="usageTracked" class="text-xs text-n-slate-10">
            {{
              t('AI_AGENTS.AI_PANEL.USAGE.DETAIL', {
                runs: fmt(metrics.usage?.last_7_days?.runs),
              })
            }}
            <template v-if="metrics.usage?.last_7_days?.estimated_cost != null">
              ·
              {{
                t('AI_AGENTS.AI_PANEL.USAGE.COST', {
                  cost: metrics.usage.last_7_days.estimated_cost,
                })
              }}
            </template>
          </p>
        </div>
      </div>
      <p
        v-if="!loadError && tab === 'ia' && metrics && usageTracked"
        class="text-xs text-n-slate-10 mt-2"
        data-testid="ai-usage-tracked-since"
      >
        {{ trackedSinceLabel }}
      </p>

      <!-- SiteUp (#3): performance da IA (derivada dos dados existentes) -->
      <div
        v-if="tab === 'ia' && metrics && metrics.ai_conversations"
        class="grid grid-cols-1 gap-3 sm:grid-cols-3 mt-3"
      >
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-teal-11"
            data-testid="ai-resolution-rate"
          >
            {{ metrics.resolution_rate }}%
          </p>
          <p class="text-xs text-n-slate-10" data-testid="ai-resolution-label">
            {{
              t('AI_AGENTS.AI_PANEL.RATES.RESOLVED', {
                count: metrics.ai_conversations,
              })
            }}
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-extrabold text-n-slate-12"
            data-testid="ai-handoff-rate"
          >
            {{ metrics.handoff_rate }}%
          </p>
          <p class="text-xs text-n-slate-10" data-testid="ai-handoff-label">
            {{ t('AI_AGENTS.AI_PANEL.RATES.WITH_ASSIGNEE') }}
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-amber-11">
            {{ metrics.csat_average != null ? metrics.csat_average : '—' }}
          </p>
          <p class="text-xs text-n-slate-10">
            CSAT médio{{ metrics.csat_count ? ` (${metrics.csat_count})` : '' }}
          </p>
        </div>
      </div>

      <!-- As duas taxas nao sao merito da IA: resolvida conta tambem a que o
           atendente resolveu, e responsavel e qualquer atribuicao. A linha diz
           como cada uma e contada. -->
      <p
        v-if="tab === 'ia' && metrics && metrics.ai_conversations"
        class="mt-2 text-xs text-n-slate-10"
        data-testid="ai-rates-help"
      >
        {{ t('AI_AGENTS.AI_PANEL.RATES.HELP') }}
      </p>

      <!-- IA-8: qualidade média auto-avaliada (LLM-judge), só aparece se houver avaliações -->
      <div
        v-if="tab === 'ia' && metrics && metrics.quality_count"
        class="grid grid-cols-1 gap-3 mt-3"
      >
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p
            class="text-2xl font-medium text-n-teal-11"
            data-testid="ai-quality-average"
          >
            {{ metrics.quality_average != null ? metrics.quality_average : '—'
            }}<span class="text-sm font-normal text-n-slate-10">/100</span>
          </p>
          <p class="text-xs text-n-slate-10">
            Qualidade média auto-avaliada ({{ metrics.quality_count }} respostas)
          </p>
        </div>
      </div>

      <PropertyFollowupPanel
        v-if="!loadError && tab === 'ia' && metrics?.property_followups"
        :data="metrics.property_followups"
        :account-id="route.params.accountId"
        :period-code="metrics.period"
      />

      <!-- A fila de revisao dos reports e a da Central da IA (status,
           categoria e nota). Aqui fica so o numero de novos e o caminho ate
           la: duas listas do mesmo dado davam estados diferentes. -->
      <div
        v-if="!loadError && tab === 'ia' && metrics && hasAiCenter"
        class="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-n-weak p-4"
        data-testid="ai-panel-feedbacks-queue"
      >
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'AI_AGENTS.AI_PANEL.FEEDBACKS_NEW',
                { count: metrics.feedbacks_new_count || 0 },
                metrics.feedbacks_new_count || 0
              )
            }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.AI_PANEL.FEEDBACKS_QUEUE_HINT') }}
          </p>
        </div>
        <router-link
          :to="aiCenterReportsRoute"
          class="inline-flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-medium text-n-brand hover:bg-n-alpha-2 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          data-testid="ai-panel-feedbacks-review-link"
        >
          {{ t('AI_AGENTS.AI_PANEL.FEEDBACKS_REVIEW') }}
          <span class="i-ph-arrow-right size-4" aria-hidden="true" />
        </router-link>
      </div>

      <div
        v-else-if="!loadError && tab === 'ia'"
        class="flex flex-col gap-2 mt-6"
      >
        <h4 class="text-xs font-medium text-n-slate-12">
          Reports de erro da IA (correções)
        </h4>
        <div v-if="feedbacks.length === 0" class="text-sm text-n-slate-11">
          Nenhum report ainda. Os atendentes registram erros pelo menu da resposta da IA.
        </div>
        <!-- A lista vem limitada aos 100 mais recentes: sem este aviso o
             cartão dizia N e a lista mostrava menos, sem explicar. -->
        <p
          v-if="metrics && feedbacks.length < (metrics.feedbacks_count || 0)"
          data-testid="ai-panel-feedbacks-truncated"
          class="text-xs text-n-slate-10"
        >
          {{
            t('AI_AGENTS.AI_PANEL.FEEDBACKS_TRUNCATED', {
              shown: feedbacks.length,
              total: metrics.feedbacks_count,
            })
          }}
        </p>
        <div
          v-for="fb in feedbacks"
          :key="fb.id"
          class="flex items-start gap-3 rounded-xl border border-n-weak p-3"
          :class="{ 'opacity-60': fb.resolved }"
        >
          <span class="i-ri-flag-line size-4 shrink-0 mt-0.5 text-n-ruby-10" />
          <div class="min-w-0 flex-1">
            <p
              class="text-sm text-n-slate-12"
              :class="{ 'line-through': fb.resolved }"
            >
              {{ fb.reason }}
            </p>
            <span class="text-[10px] text-n-slate-10">
              {{ feedbackMetadata(fb) }}
            </span>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-md px-2.5 py-1 text-xs font-medium"
            :class="
              fb.resolved
                ? 'text-n-slate-11 hover:bg-n-alpha-2'
                : 'bg-n-teal-9/15 text-n-teal-11'
            "
            @click="resolveFeedback(fb)"
          >
            {{ fb.resolved ? 'Reabrir' : 'Resolver' }}
          </button>
        </div>
      </div>
    </template>
  </component>
</template>
